import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "react-bootstrap";
import { RekeyAndChangeRecoveryRequest } from "@preveil-api";
import {
  Account, GetUserRequestsApiResponse, Message, MessageHandlerDisplayType, useAppDispatch, useAppSelector, useGetUserRequestsMutation, usePostUsersFindMutation, UserRequest, Pagination,
  GlobalConstants, PrivateRoutes, isSameUser
} from "src/common";
import { Loading, PageHeader } from "src/components";
import { RecoveryGroupForm, UserRequests, Notifications } from ".";
import { settingsActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

function RecoveryComponent() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const is_org = useAppSelector((state: RootState) => state.account.current_account?.org_info);
  const current_recovery_group = useAppSelector((state: RootState) => state.account.current_account?.recovery_group);
  const [pending_requests, setPendingRequests] = useState<UserRequest[]>([]);
  const [history_requests, setHistoryRequests] = useState<UserRequest[]>([]);
  const [submitting_form, setSubmittingForm] = useState<boolean>(false);
  const [disable_edits, setDisableEdits] = useState<boolean>(false);
  const [optionals_required, setOptionalsRequired] = useState<number>(1);
  const [rg_loading, setRGLoading] = useState<boolean>(true);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [findUsers] = usePostUsersFindMutation();
  const [getRequests] = useGetUserRequestsMutation();

  /* On initial Load: Get Recovery Group and get pending user requests for the warning toast */
  useEffect(() => {
    getUserRequests(false, "pending", true);
  }, []);

  /* Description: when current_recovery_group is populated/changed - set whether the user can edit it or not
  depending on the number of optional users and whether the user is in an organization. (Users in organizations)
  cannot edit their recovery group. */
  useEffect(() => {
    current_recovery_group && setRGLoading(false);
    if (is_org) {
      setDisableEdits(true);
    }
  }, [current_recovery_group]);

  /* Description: Gets the user requests, creates a new UserRequest object for each request and populates the certain properties of the
  user request object. */
  async function getUserRequests(
    history: boolean,
    status: string,
    hide_expired: boolean,
    limit?: number,
    offset?: number,
  ) {
    if (!!current_account) {
      await getRequests({ userId: current_account.user_id, status, hideExpired: hide_expired, limit, offset })
        .unwrap()
        .then(async (response: GetUserRequestsApiResponse) => {
          const user_requests = _.uniqBy(
            response.requests.map((request) => {
              const new_request = new UserRequest(
                request.request_id,
                request.metadata,
                request.payload,
                request.status,
                request.requester,
              );
              const l_request = pending_requests.find(
                (local_request) => local_request.request_id === request.request_id,
              );
              if (!!l_request) {
                new_request.request_responses = l_request.request_responses;
              }
              return new_request;
            }),
            (req) => req.request_id,
          );
          let user_ids = new Array<string>();
          user_requests.forEach((request) => {
            const parsed = request.parsed_payload.data as RekeyAndChangeRecoveryRequest;
            user_ids = user_ids.concat(request.requester_id);
            if (!!parsed.group) {
              user_ids = user_ids.concat(
                parsed.group.map((approver: { user_id: string }) => approver.user_id),
              );
            }
          });
          user_ids = _.uniqBy(user_ids, (id) => id);
          const requests = await getUsers(user_ids, user_requests);
          if (!!requests) {
            if (history) {
              setHistoryRequests(requests);
            }
            const total_page_rows = requests.length;
            const total_rows = response.total_rows || requests.length;
            if (total_page_rows > 0 && total_rows > 0) {
              const pagination_item = new Pagination(
                page,
                total_page_rows,
                total_rows,
                GlobalConstants.REQUESTS_PER_PAGE_COUNT,
              ).pagination_item;
              !!pagination_item && dispatch(settingsActions.setPagination(pagination_item));
            } else {
              dispatch(settingsActions.resetPagination());
              setPage(0);
            }
            !history && setPendingRequests(requests);
          }
          setIsLoading(false);
        })
        .catch((stack) => {
          dispatch(
            uiActions.handleRequestErrors(new Message(stack, MessageHandlerDisplayType.logger)),
          );
        });
    }
  }

  /* Description: Returns an array of UserRequest Objects. It gets user objects from the backend based on the requester_ids
  and sets UserProfile arrays for approvers and requesters. */
  async function getUsers(
    user_ids: string[],
    user_requests: UserRequest[],
  ): Promise<UserRequest[] | undefined> {
    if (!!current_account) {
      return await findUsers({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: { spec: user_ids.map((id) => ({ user_id: id, key_version: -1 })) },
      })
        .unwrap()
        .then(({ users }) => {
          const updated_user_requests = user_requests.map((request) => {
            const u = users.find((u) => isSameUser(u.user_id, request.requester_id));
            request.requester = u
              ? { name: u.display_name, address: u.user_id, external_email: u.external_email }
              : null;
            const parsed = request.parsed_payload.data as RekeyAndChangeRecoveryRequest;
            if (!!parsed.group) {
              request.approvers = parsed.group.map((approver) => {
                const u = users.find((u) => isSameUser(u.user_id, approver.user_id));
                return { name: u?.display_name || null, address: u?.user_id || "" };
              });
            }
            return request;
          });
          return updated_user_requests;
        })
        .catch((msg: string) => {
          dispatch(
            uiActions.handleRequestErrors(new Message(msg, MessageHandlerDisplayType.logger)),
          );
          const updated_response = user_requests.map((request) => {
            if (!!request.parsed_payload.approvers) {
              request.approvers = request.parsed_payload.approvers.map((approver) => ({
                name: null,
                address: approver.user_id,
              }));
            }
            return request;
          });
          return updated_response;
        });
    }
    return undefined;
  }

  /* Description: Navigate to the recovery requests page on settings - this is done when
  the user clicks on the "number of updates" button on the toast. */
  function goToPendingRequests() {
    navigate(`/settings/${PrivateRoutes.recovery_requests_route}`);
  }

  return (
    <>
      {window.location.pathname.match("requests") ? (
        <UserRequests
          getUserRequests={getUserRequests}
          is_loading={is_loading}
          setIsLoading={setIsLoading}
          pending_requests={pending_requests}
          setPendingRequests={setPendingRequests}
          history_requests={history_requests}
          setHistoryRequests={setHistoryRequests}
          current_recovery_group={current_recovery_group}
          page={page}
          setPage={setPage}
        ></UserRequests>
      ) : (
        <>
          <PageHeader>
            <h1>Recovery Group</h1>
          </PageHeader>
          {!!submitting_form || !!rg_loading ? (
            <Loading></Loading>
          ) : (
            <Card className="card-section">
              <Notifications
                current_recovery_group={current_recovery_group}
                pending_requests={pending_requests}
                disable_edits={disable_edits}
                goToPendingRequests={goToPendingRequests}
              ></Notifications>
              <RecoveryGroupForm
                disable_edits={disable_edits}
                setSubmittingForm={setSubmittingForm}
                setOptionalsRequired={setOptionalsRequired}
                optionals_required={optionals_required}
                submitting_form={submitting_form}
                current_recovery_group={current_recovery_group}
                pending_requests={pending_requests}
                setPendingRequests={setPendingRequests}
              ></RecoveryGroupForm>
            </Card>
          )}
        </>
      )}
    </>
  );
}

export default React.memo(RecoveryComponent);
